import { firstPresent } from '@federa/const/dual-spelling';
import { coalesceStringAllowEmpty } from '@federa/utils/data/coalesce-string-allow-empty';
import { FIRST_PRESENT_LAST_READ_TIME } from '@federa/const/first-present-values';
import { bareNumberOrNull, numberOrZero } from '@federa/const/number-guards';
import { toParticipant } from './messagingClient.parsers.ts';
import type { ChatRoomStruct } from '@federa/types/conversations-chat-structs';
import type { RawChatRoom, RawConversationResponse } from '@federa/types/ui-chat-messaging';

/** Last-message timing/unread fields (snake/camel raw fallbacks; complexity extraction from toChatRoom). Every dual-spelling
 *  read goes through `firstPresent` -- `??` semantics, preserved verbatim from the retired inline chains. The
 *  `coalesceStringAllowEmpty` calls stay as they are: that helper KEEPS an empty string, a third semantic neither reader here
 *  provides. */
function toChatRoomTiming(
	raw: RawChatRoom,
): Pick<ChatRoomStruct, 'lastReadTime' | 'lastMessageTime' | 'lastMessageTimestamp' | 'unreadCount'> {
	// `lastMessageTimeStamp` is NOT the camel twin of `last_message_timestamp` (capital `S`) -- both wire spellings are live,
	// so the pair is passed explicitly rather than derived.
	const lastTimestamp = firstPresent(raw, 'last_message_timestamp', 'lastMessageTimeStamp');
	const lastMessageTimeIso = coalesceStringAllowEmpty(raw.last_message_time, raw.lastMessageTime) ?? '';
	return {
		lastReadTime: firstPresent(raw, FIRST_PRESENT_LAST_READ_TIME, 'lastReadTime') ?? null,
		lastMessageTime: lastMessageTimeIso || null,
		lastMessageTimestamp: bareNumberOrNull(lastTimestamp),
		unreadCount: numberOrZero(raw.unreadCount),
	};
}

export function toChatRoom(raw: RawChatRoom): ChatRoomStruct {
	const participants = Array.isArray(raw.participants) ? raw.participants.map(toParticipant) : [];
	return {
		id: raw.id ?? '',
		title: coalesceStringAllowEmpty(raw.friendly_name, raw.friendlyName) ?? 'Conversation',
		participants,
		lastMessage: firstPresent(raw, 'last_message', 'lastMessage') ?? null,
		conversationType: coalesceStringAllowEmpty(raw.conversation_type, raw.conversationType) ?? '',
		createdAt: coalesceStringAllowEmpty(raw.created_at, raw.createdAtTime) ?? '',
		lastMessageId: coalesceStringAllowEmpty(raw.last_message_id, raw.lastMessageId) ?? '',
		...toChatRoomTiming(raw),
	};
}

export function collectRooms(body: RawConversationResponse | null | undefined): ChatRoomStruct[] {
	if (!body || !Array.isArray(body.personal)) return [];
	return body.personal.map(toChatRoom);
}
